import { fetchJson } from "./http";

const API_URL = "https://lborrito.de/api/v1/contacts";

export async function fetchContacts({ cursor = null, limit = 20, q = "", filters = {} } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.append("cursor", cursor);
  if (limit) params.append("limit", limit);
  if (q) params.append("q", q);
  
  // Filter-Parameter mitschicken
  if (filters.hasEmail && filters.hasEmail !== "both") {
    params.append("hasEmail", filters.hasEmail);
  }
  if (filters.hasPhone && filters.hasPhone !== "both") {
    params.append("hasPhone", filters.hasPhone);
  }
  
  const normalizeArray = (value) => {
    const splitValue = (item) => {
      if (typeof item !== "string") return [item];
      return item
        .split(/[\s,]+/)
        .map((v) => v.trim())
        .filter(Boolean);
    };

    const raw = Array.isArray(value) ? value.flatMap(splitValue) : splitValue(value);
    return raw.filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
  };

  // Arrays: komma-separierte Werte
  const customerTypes = normalizeArray(filters.customerTypes);
  const contactMethods = normalizeArray(filters.contactMethods);
  const contactOwners = normalizeArray(filters.contactOwners);
  const tags = normalizeArray(filters.tags);

  if (customerTypes.length > 0) {
    params.append("customerTypeIds", customerTypes.join(","));
  }
  if (contactMethods.length > 0) {
    params.append("contactMethodIds", contactMethods.join(","));
  }
  if (contactOwners.length > 0) {
    params.append("ownerUserIds", contactOwners.join(","));
  }
  if (tags.length > 0) {
    params.append("tags", tags.join(","));
  }
  
  if (filters.hasEvent && filters.hasEvent !== "both") {
    params.append("hasEvent", filters.hasEvent);
  }
  if (filters.canContact && filters.canContact !== "both") {
    params.append("canContact", filters.canContact);
  }

  const url = `${API_URL}/stream?${params.toString()}`;
  return fetchJson(url, {
    method: "GET",
  });
}

export async function fetchContactDetail(id) {
  return fetchJson(`${API_URL}/${id}/detail`, {
    method: "GET",
  });
  /*
    -> ContactDetailDto {
         id, name, company, email, phone, customerInfo, canContact,
         events: [ {id, day, description}, ... ],
         customerTypes: [ {id, type}, ... ],
         contactMethods: [ {id, method}, ... ],
         owners: [ {id, username, email}, ... ],
         tags: [ {id, label, active, name, type, value}, ... ]
       }
  */
}

/**
 * 🔐 PATCH /api/v1/contacts/{contactId}
 * Aktualisiert Kontakt-Felder (Name, Firma, Email, Telefon, CustomerInfo, etc.)
 * Auto-Save wird mit Debouncing aufgerufen
 */
export async function updateContact(contactId, updateData) {
  const bodyObj = {};
  
  // Nur nicht-null Felder mitschicken
  if (updateData.name !== undefined) bodyObj.name = updateData.name;
  if (updateData.company !== undefined) bodyObj.company = updateData.company;
  if (updateData.address !== undefined) bodyObj.address = updateData.address;
  if (updateData.email !== undefined) bodyObj.email = updateData.email;
  if (updateData.phone !== undefined) bodyObj.phone = updateData.phone;
  if (updateData.customerInfo !== undefined) bodyObj.customerInfo = updateData.customerInfo;
  if (updateData.ownerIdChange !== undefined) bodyObj.ownerIdChange = updateData.ownerIdChange;
  if (updateData.customerTypeIdChange !== undefined) bodyObj.customerTypeIdChange = updateData.customerTypeIdChange;
  if (updateData.contactMethodIdChange !== undefined) bodyObj.contactMethodIdChange = updateData.contactMethodIdChange;
  if (updateData.canContact !== undefined) bodyObj.canContact = updateData.canContact;

  return fetchJson(`${API_URL}/${contactId}`, {
    method: "PATCH",
    body: bodyObj,
  });
}

/**
 * 🔐 DELETE /api/v1/contacts/{contactId}/field/{fieldName}
 * Leert ein Feld (phone, email, company, customerinfo)
 */
export async function clearContactField(contactId, fieldName) {
  return fetchJson(`${API_URL}/${contactId}/field/${fieldName}`, {
    method: "DELETE",
  });
}

// Optionen laden: Customer Types, Contact Methods, Owners
export async function fetchCustomerTypeOptions(contactId) {
  return fetchJson(`${API_URL}/${contactId}/options/customer-types`, {
    method: "GET",
  }); // -> { items: [{id, label, assigned}] }
}

export async function fetchContactMethodOptions(contactId) {
  return fetchJson(`${API_URL}/${contactId}/options/contact-methods`, {
    method: "GET",
  }); // -> { items: [{id, label, assigned}] }
}

export async function fetchOwnerOptions(contactId) {
  return fetchJson(`${API_URL}/${contactId}/options/owners`, {
    method: "GET",
  }); // -> { items: [{id, label, assigned}] }
}

// Tag an Kontakt anhängen (Body optional: { tagLabel, tagValue, tagDescription, active })
export async function attachTag(contactId, tagId, body = null) {
  await fetchJson(`${API_URL}/${contactId}/tags/${tagId}`, {
    method: "PUT",
    body, // fetchJson übernimmt JSON.stringify + X-XSRF-TOKEN
  });
}

// Tag von Kontakt entfernen (Hard Delete)
export async function detachTag(contactId, tagId) {
  await fetchJson(`${API_URL}/${contactId}/tags/${tagId}`, {
    method: "DELETE",
  });
}

/**
 * POST /api/v1/contacts
 * Erstellt einen neuen Kontakt
 */
export async function createContact(contactData) {
  return fetchJson(`${API_URL}`, {
    method: "POST",
    body: contactData,
  });
}

/**
 * 🔐 DELETE /api/v1/contacts/{contactId}
 * Löscht einen kompletten Kontakt
 */
export async function deleteContact(contactId) {
  return fetchJson(`${API_URL}/${contactId}`, {
    method: "DELETE",
  });
}

/**
 * POST /api/v1/options/customer-types
 * Erstellt einen neuen Customer Type
 */
export async function createCustomerType(name) {
  return fetchJson(`https://lborrito.de/api/v1/options/customer-types`, {
    method: "POST",
    body: { name },
  });
}

/**
 * POST /api/v1/options/contact-methods
 * Erstellt eine neue Contact Method
 */
export async function createContactMethod(name) {
  return fetchJson(`https://lborrito.de/api/v1/options/contact-methods`, {
    method: "POST",
    body: { name },
  });
}

/**
 * GET /api/v1/options/customer-types
 * Holt alle Customer Types für Filter
 */
export async function getAllCustomerTypes() {
  return fetchJson(`https://lborrito.de/api/v1/options/customer-types`, {
    method: "GET",
  });
}

/**
 * GET /api/v1/options/contact-methods
 * Holt alle Contact Methods für Filter
 */
export async function getAllContactMethods() {
  return fetchJson(`https://lborrito.de/api/v1/options/contact-methods`, {
    method: "GET",
  });
}

/**
 * GET /api/v1/options/owners
 * Holt alle Owners für Filter
 */
export async function getAllOwners() {
  return fetchJson(`https://lborrito.de/api/v1/options/owners`, {
    method: "GET",
  });
}

